import React, { useId, useState } from 'react';
import { Tooltip } from 'antd';

import { useUIColors } from '../../theme';
import { cx, defineRecipe, useRecipe, type Styleable } from '../../variants';

/**
 * `yes`/`no`, not booleans — the string-keyed override registry couldn't
 * address a real boolean axis (same reasoning as `SidebarItem`'s `active`).
 */
export const optionCardRecipe = defineRecipe('optionCard', 'ui-option-card', {
    variants: {
        selected: { yes: 'ui-option-card--selected', no: '' },
        disabled: { yes: 'ui-option-card--disabled', no: '' },
    },
    defaultVariants: { selected: 'no', disabled: 'no' },
});

export interface OptionCardProps extends Styleable {
    title: React.ReactNode;
    description?: React.ReactNode;
    selected: boolean;
    onSelect: () => void;
    /** `'radio'` for a mutually-exclusive group, `'checkbox'` for a single
     *  on/off card. Defaults to `'radio'`. */
    inputType?: 'radio' | 'checkbox';
    /** Required for `'radio'` — groups sibling cards the same way a native
     *  radio group does. Unused for `'checkbox'`. */
    name?: string;
    value?: string;
    disabled?: boolean;
    /** Tooltip shown while disabled, explaining why — the reason a caller
     *  disables one option instead of omitting it. */
    disabledReason?: React.ReactNode;
}

/**
 * A bordered, titled choice — the "radio card" pattern repeated by hand at
 * least twice in the portal console (`apps-scope-radio__option` in
 * `AppsCreatePage`, `portal-console-radio`/`acc-reset-mode` in
 * `ResetCredentialFields`) with near-identical markup and near-identical CSS,
 * one copy of each slightly different from the other. Tier-1: colours come
 * from `useUIColors()`, this package ships no CSS, and the console's exact
 * spacing/type values belong to callers, not here.
 *
 * `selected` decides the highlight directly from the prop the caller already
 * computes (`checked === value`), not from a `:has(input:checked)` CSS
 * selector — the pattern this replaces used `:has()` for exactly this, which
 * depends on the DOM `checked` PROPERTY staying in sync with the CSS engine's
 * own match on every re-render. A prop the component already owns is not at
 * the mercy of that sync.
 *
 * A `disabled` card wraps in an antd `Tooltip` only when `disabledReason` is
 * given — the same "explain the reason, not just the condition" rule
 * `Callout`'s own doc states, applied to why an option is unavailable rather
 * than to a callout's message.
 */
export function OptionCard({
    title,
    description,
    selected,
    onSelect,
    inputType = 'radio',
    name,
    value,
    disabled = false,
    disabledReason,
    className,
    style,
}: OptionCardProps): React.ReactElement {
    const colors = useUIColors();
    const titleId = useId();
    const [hovered, setHovered] = useState(false);
    const recipeClass = useRecipe(optionCardRecipe, {
        selected: selected ? 'yes' : 'no',
        disabled: disabled ? 'yes' : 'no',
    });

    const borderColor = selected ? colors.primary : hovered && !disabled ? colors.controlRing : colors.border;

    const card = (
        <label
            className={cx(recipeClass, className)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '12px 14px',
                border: `1px solid ${borderColor}`,
                borderRadius: 8,
                background: selected ? colors.ctaLight : hovered && !disabled ? colors.background : 'transparent',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                transition: 'border-color 120ms ease-out, background 120ms ease-out',
                ...style,
            }}
        >
            <input
                type={inputType}
                name={name}
                value={value}
                checked={selected}
                disabled={disabled}
                onChange={() => {
                    if (!disabled) onSelect();
                }}
                aria-labelledby={titleId}
                style={{ marginTop: 2, accentColor: colors.primary, cursor: disabled ? 'not-allowed' : 'pointer' }}
            />
            <span style={{ fontSize: 14, lineHeight: 1.5, color: colors.textSecondary }}>
                <strong
                    id={titleId}
                    style={{
                        display: 'block',
                        marginBottom: description ? 4 : 0,
                        fontSize: 15,
                        fontWeight: 700,
                        color: colors.text,
                    }}
                >
                    {title}
                </strong>
                {description}
            </span>
        </label>
    );

    if (disabled && disabledReason) {
        return <Tooltip title={disabledReason}>{card}</Tooltip>;
    }
    return card;
}

export default OptionCard;
